import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadForOfflineOutlinedIcon from "@mui/icons-material/DownloadForOfflineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { PageTitle, SectionContainer } from "../Components/StyledComponents";
import { deleteProject, loginWithPin, publishProject, updateProject } from "../api/adminGallery";
import { loadGallery } from "../api/gallery";
import InvoiceWizard from "../Components/Admin/InvoiceWizard";
import InvoiceOverview from "../Components/Admin/InvoiceOverview";
import Seo from "../Components/Seo";

const fileInputStyles = {
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
  p: 2.5,
  border: "1px dashed rgba(36,53,45,0.22)",
  borderRadius: 2,
  backgroundColor: "rgba(255,255,255,0.7)",
};

const ROLE_OPTIONS = [
  { value: "before", label: "Voor" },
  { value: "after", label: "Na" },
  { value: "detail", label: "Extra" },
];

const buildSelectionItem = (file, index) => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${index}`,
  file,
  role: "detail",
  previewUrl: URL.createObjectURL(file),
});

const InstallAppCard = ({
  canInstall,
  installingApp,
  isStandalone,
  onInstall,
}) => {
  if (isStandalone) {
    return null;
  }

  return (
    <Alert
      severity="info"
      icon={<DownloadForOfflineOutlinedIcon fontSize="inherit" />}
      action={
        canInstall ? (
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={onInstall}
            disabled={installingApp}
            sx={{ whiteSpace: "nowrap" }}
          >
            {installingApp ? "Bezig..." : "Installeer app"}
          </Button>
        ) : null
      }
    >
      {canInstall
        ? "Zet deze pagina op het beginscherm. Dan opent hij straks als een app direct in het adminportaal."
        : "Wil je deze pagina als app op het beginscherm zetten? Open het browsermenu en kies 'Toevoegen aan startscherm' of 'Install app'."}
    </Alert>
  );
};

const Admin = () => {
  const [pin, setPin] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [adminView, setAdminView] = useState("projects");
  const [saving, setSaving] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [existingProjects, setExistingProjects] = useState([]);
  const [busyProjectId, setBusyProjectId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [installingApp, setInstallingApp] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const selectedImagesRef = useRef([]);

  useEffect(() => {
    selectedImagesRef.current = selectedImages;
  }, [selectedImages]);

  useEffect(() => () => {
    selectedImagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
  }, []);

  useEffect(() => {
    const checkStandalone = () => {
      setIsStandalone(
        window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true
      );
    };

    checkStandalone();

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredInstallPrompt(event);
    };

    const handleAppInstalled = () => {
      setDeferredInstallPrompt(null);
      setInstallingApp(false);
      setIsStandalone(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const refreshProjects = async () => {
    setLoadingProjects(true);

    try {
      const gallery = await loadGallery();
      setExistingProjects(
        (gallery.projects || []).map((project) => ({
          id: project.id,
          title: project.title || "",
          description: project.description || "",
          before: project.before,
          after: project.after,
        }))
      );
    } catch (loadError) {
      setError(loadError.message || "Projecten laden is niet gelukt.");
    } finally {
      setLoadingProjects(false);
    }
  };

  const resetForm = () => {
    selectedImages.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    setTitle("");
    setDescription("");
    setSelectedImages([]);
  };

  const handleFileSelection = (files) => {
    const newItems = files.map((file, index) => buildSelectionItem(file, index));
    setSelectedImages((currentImages) => [...currentImages, ...newItems]);
  };

  const updateRole = (id, role) => {
    if (!role) {
      return;
    }

    setSelectedImages((currentImages) =>
      currentImages.map((image) => (image.id === id ? { ...image, role } : image))
    );
  };

  const removeImage = (id) => {
    setSelectedImages((currentImages) => {
      const imageToRemove = currentImages.find((image) => image.id === id);

      if (imageToRemove) {
        URL.revokeObjectURL(imageToRemove.previewUrl);
      }

      return currentImages.filter((image) => image.id !== id);
    });
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoggingIn(true);
    setError("");
    setSuccess("");

    try {
      await loginWithPin(pin);
      setIsAuthenticated(true);
      await refreshProjects();
    } catch (loginError) {
      setError(loginError.message || "Inloggen is niet gelukt.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setSuccess("");
    setError("");
  };

  const handleInstallApp = async () => {
    if (!deferredInstallPrompt) {
      return;
    }

    setInstallingApp(true);

    try {
      await deferredInstallPrompt.prompt();
      await deferredInstallPrompt.userChoice;
    } finally {
      setDeferredInstallPrompt(null);
      setInstallingApp(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const project = await publishProject({
        title,
        description,
        pin,
        images: selectedImages,
      });

      setSuccess(`Project "${project.title}" staat nu op de website.`);
      resetForm();
      await refreshProjects();
    } catch (submitError) {
      setError(submitError.message || "Opslaan is niet gelukt.");
    } finally {
      setSaving(false);
    }
  };

  const handleExistingProjectChange = (projectId, field, value) => {
    setExistingProjects((currentProjects) =>
      currentProjects.map((project) =>
        project.id === projectId
          ? { ...project, [field]: value }
          : project
      )
    );
  };

  const handleSaveProject = async (projectId) => {
    const project = existingProjects.find((item) => item.id === projectId);

    if (!project) {
      return;
    }

    setBusyProjectId(projectId);
    setError("");
    setSuccess("");

    try {
      await updateProject({
        pin,
        projectId,
        title: project.title,
        description: project.description,
      });
      setSuccess(`Project "${project.title}" is bijgewerkt.`);
      await refreshProjects();
    } catch (saveError) {
      setError(saveError.message || "Project bijwerken is niet gelukt.");
    } finally {
      setBusyProjectId("");
    }
  };

  const handleDeleteProject = async (projectId) => {
    const project = existingProjects.find((item) => item.id === projectId);

    if (!project) {
      return;
    }

    const confirmed = window.confirm(`Weet je zeker dat je "${project.title}" wilt verwijderen?`);

    if (!confirmed) {
      return;
    }

    setBusyProjectId(projectId);
    setError("");
    setSuccess("");

    try {
      await deleteProject({ pin, projectId });
      setSuccess(`Project "${project.title}" is verwijderd.`);
      await refreshProjects();
    } catch (deleteError) {
      setError(deleteError.message || "Project verwijderen is niet gelukt.");
    } finally {
      setBusyProjectId("");
    }
  };

  if (!isAuthenticated) {
    return (
      <SectionContainer>
        <Seo
          title="Admin | Meester Stoffeerders"
          description="Beveiligde adminomgeving van Meester Stoffeerders."
          path="/admin"
          noindex
        />
        <Container maxWidth="sm">
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
              <Stack spacing={2.5} component="form" onSubmit={handleLogin}>
                <Box sx={{ textAlign: "center" }}>
                  <PageTitle>Admin Inloggen</PageTitle>
                  <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 520, mx: "auto", mt: 3 }}>
                    Voer de pincode in om projecten toe te voegen aan de website.
                  </Typography>
                </Box>

                <InstallAppCard
                  canInstall={Boolean(deferredInstallPrompt)}
                  installingApp={installingApp}
                  isStandalone={isStandalone}
                  onInstall={handleInstallApp}
                />

                {error ? <Alert severity="error">{error}</Alert> : null}

                <TextField
                  label="Pincode"
                  type="password"
                  value={pin}
                  onChange={(event) => setPin(event.target.value)}
                  autoComplete="current-password"
                  required
                  fullWidth
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={loggingIn}
                  startIcon={loggingIn ? <CircularProgress size={18} color="inherit" /> : <LockOutlinedIcon />}
                >
                  {loggingIn ? "Bezig met inloggen..." : "Inloggen"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Container>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer>
      <Seo
        title="Admin | Meester Stoffeerders"
        description="Beveiligde adminomgeving van Meester Stoffeerders."
        path="/admin"
        noindex
      />
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <PageTitle>Project Plaatsen</PageTitle>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 760, mx: "auto", mt: 3 }}>
            Kies wat je wilt doen: projecten beheren, een factuur opstellen of bestaande facturen controleren.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <InstallAppCard
            canInstall={Boolean(deferredInstallPrompt)}
            installingApp={installingApp}
            isStandalone={isStandalone}
            onInstall={handleInstallApp}
          />
        </Box>

        <ToggleButtonGroup
          exclusive
          value={adminView}
          onChange={(_, value) => {
            if (value) {
              setAdminView(value);
            }
          }}
          sx={{ mb: 3 }}
        >
          <ToggleButton value="projects">Projecten</ToggleButton>
          <ToggleButton value="invoices">Factuur Wizard</ToggleButton>
          <ToggleButton value="invoice-overview">Factuuroverzicht</ToggleButton>
        </ToggleButtonGroup>

        {adminView === "projects" ? (
          <>
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Stack spacing={2.5} component="form" onSubmit={handleSubmit}>
              {error ? <Alert severity="error">{error}</Alert> : null}
              {success ? (
                <Alert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />}>
                  {success}
                </Alert>
              ) : null}

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Typography variant="body2" color="text.secondary">
                  Pincode is gecontroleerd. Na het plaatsen werkt de website automatisch bij.
                </Typography>
                <Button color="inherit" onClick={handleLogout}>
                  Uitloggen
                </Button>
              </Box>

              <TextField
                label="Titel van het project"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Bijvoorbeeld: Bank opnieuw gestoffeerd in Almere"
                required
                fullWidth
              />

              <TextField
                label="Korte beschrijving"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Bijvoorbeeld: Nieuwe stof, nieuwe vulling en versteviging van de zitting."
                multiline
                minRows={3}
                fullWidth
              />

              <Box sx={fileInputStyles}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>
                  Alle foto's
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Kies alle foto's in een keer. Daarna kun je onder elke foto aangeven of het een voor-foto, na-foto of extra foto is.
                </Typography>
                <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} sx={{ alignSelf: "flex-start" }}>
                  Kies foto's
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(event) => {
                      handleFileSelection(Array.from(event.target.files || []));
                      event.target.value = "";
                    }}
                  />
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {selectedImages.length === 0 ? "Nog geen foto's gekozen" : `${selectedImages.length} foto${selectedImages.length > 1 ? "'s" : ""} gekozen`}
                </Typography>
              </Box>

              {selectedImages.length > 0 ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 2,
                  }}
                >
                  {selectedImages.map((image) => (
                    <Card key={image.id} sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid rgba(36,53,45,0.08)" }}>
                      <Box
                        component="img"
                        src={image.previewUrl}
                        alt={image.file.name}
                        sx={{
                          width: "100%",
                          height: 220,
                          objectFit: "contain",
                          backgroundColor: "background.default",
                          display: "block",
                        }}
                      />
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}>
                          {image.file.name}
                        </Typography>
                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          value={image.role}
                          onChange={(_, role) => updateRole(image.id, role)}
                          size="small"
                          sx={{ mb: 1.5 }}
                        >
                          {ROLE_OPTIONS.map((option) => (
                            <ToggleButton key={option.value} value={option.value}>
                              {option.label}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                        <Button
                          color="inherit"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => removeImage(image.id)}
                          sx={{ width: "100%", justifyContent: "center" }}
                        >
                          Verwijderen
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : null}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                  pt: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Minstens 1 foto moet op `Na` staan.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Deze versie controleert de pincode direct via de backend en gebruikt geen SAS-token in de browser.
                  </Typography>
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                >
                  {saving ? "Bezig met plaatsen..." : "Plaatsen"}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, mt: 4 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main" }}>
                  Bestaande Projecten
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Pas titel en beschrijving aan of verwijder een project helemaal. Foto's vervangen doen we in de volgende stap.
                </Typography>
              </Box>

              {loadingProjects ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : existingProjects.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Er zijn nog geen projecten gevonden.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {existingProjects.map((project) => (
                    <Card key={project.id} sx={{ borderRadius: 2, border: "1px solid rgba(36,53,45,0.08)", boxShadow: "none" }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                            {project.before?.src ? (
                              <Box component="img" src={project.before.src} alt={project.title} sx={{ width: 88, height: 88, objectFit: "contain", backgroundColor: "background.default", borderRadius: 2 }} />
                            ) : null}
                            {project.after?.src ? (
                              <Box component="img" src={project.after.src} alt={project.title} sx={{ width: 88, height: 88, objectFit: "contain", backgroundColor: "background.default", borderRadius: 2 }} />
                            ) : null}
                          </Box>

                          <TextField
                            label="Titel"
                            value={project.title}
                            onChange={(event) => handleExistingProjectChange(project.id, "title", event.target.value)}
                            fullWidth
                          />

                          <TextField
                            label="Beschrijving"
                            value={project.description}
                            onChange={(event) => handleExistingProjectChange(project.id, "description", event.target.value)}
                            multiline
                            minRows={3}
                            fullWidth
                          />

                          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                              {project.id}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                              <Button
                                variant="outlined"
                                startIcon={<SaveOutlinedIcon />}
                                onClick={() => handleSaveProject(project.id)}
                                disabled={busyProjectId === project.id}
                              >
                                Opslaan
                              </Button>
                              <Button
                                color="inherit"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => handleDeleteProject(project.id)}
                                disabled={busyProjectId === project.id}
                              >
                                Verwijderen
                              </Button>
                            </Box>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
          </>
        ) : adminView === "invoices" ? (
          <InvoiceWizard pin={pin} />
        ) : (
          <InvoiceOverview pin={pin} />
        )}
      </Container>
    </SectionContainer>
  );
};

export default Admin;
